<template>
  <v-dialog
    v-model="show"
    @click:outside="hideVideoUploadDialog"
    width="500"
  >
    <v-card>
      <v-card-title/>
      <v-card-text>
        <v-container>
          <v-alert
            v-if="!sourceId"
            type="info"
            dense
            outlined
          >
            Please, select the source
          </v-alert>
          <v-alert
            v-if="isFileSelected &&
                isResolutionOk && !isVideoPreprocessError"
            type="success"
            dense
            outlined
          >
            Video has suitable resolution
          </v-alert>
          <v-alert
            v-if="isFileSelected &&
                !isResolutionOk && !isVideoPreprocessError"
            type="error"
            dense
            outlined
          >
            Video resolution must be at least 720p
          </v-alert>
          <v-alert
            v-if="isFileSelected && isVideoPreprocessError"
            type="warning"
            dense
            outlined
          >
            Can't get video info, please check video status after upload
            or try encode video with H.264 codec
          </v-alert>
          <v-row v-if="isDirectUpload">
            <vimeo-input
              :sourceId="sourceId"
              :priority-id="priorityId"
              :is-enable-poi-after-upload="isEnablePoiAfterUpload"
              @loading="vimeoLoading"
              @clear="vimeoClear"
              @fileselected="vimeoFileSelected"
              @saved="addVideoItem"
              @error="vimeoError"
              ref="poiVimeoDirectUpload"
            />
          </v-row>
          <v-row v-else>
            <v-file-input
              v-model="file"
              :loading="loading"
              :disabled="disabled"
              accept=".mp4"
              label="Click here to upload new .mp4 file"
            />
          </v-row>
          <v-row>
            <v-col
              cols="12"
            >
              <DataList
                :item-model.sync="sourceId"
                :type-object="$constants.DATA_LISTS.VIDEO_SOURCES_LIST"
                label="source"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
            >
              <DataList
                :item-model.sync="priorityId"
                :type-object="$constants.DATA_LISTS.VIDEO_PRIORITIES_LIST"
                label="display order"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="blue darken-1"
          text
          @click="hideVideoUploadDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          color="blue darken-1"
          textVideoPriorityList
          :disabled="!enableUpload"
          @click="uploadVideo"
        >
          Upload
        </v-btn>
        <v-switch
          v-if="!isPoiEnabled"
          v-model="isEnablePoiAfterUpload"
          :disabled="!enableUpload"
          label="try to enable POI after video upload"
        />
        <span v-else>POI is already enabled</span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DataList from '../DataLists/DataList.vue';
import VimeoInput from '../VimeoInput.vue';
import videoCheckMixin from '../../mixins/videoCheckMixin';
import fetchErrorMixin from '../../mixins/fetchErrorMixin';

export default {
  name: 'DialogVideoUpload',

  components: {
    DataList,
    VimeoInput,
  },
  mixins: [videoCheckMixin, fetchErrorMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    poiId: {
      type: Number,
      default: NaN,
    },
    isDirectUpload: {
      type: Boolean,
      default: false,
    },
    isPoiEnabled: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
    file: null,
    disabled: false,
    isUploadStart: false,
    isFileSelected: false,
    loading: false,
    sourceId: null,
    priorityId: 5,
    isResolutionOk: false,
    isVideoPreprocessError: false,
    isEnablePoiAfterUpload: false,
  }),

  methods: {
    vimeoLoading() {
    },

    vimeoClear() {
      this.isFileSelected = false;
    },

    vimeoFileSelected(event) {
      this.preprocessVideoFile(event);
    },

    vimeoError(event) {
      this.$emit('errorUpload', event);
    },

    uploadVideo() {
      if (this.show && this.isFileSelected && this.sourceId) {
        this.isUploadStart = true;
        if (this.isDirectUpload) {
          this.$refs.poiVimeoDirectUpload.uploadVideo();
        } else {
          this.loading = true;
          this.disabled = true;

          const data = new FormData();

          data.append('mp4', this.file);
          data.append('poiId', this.poiId.toString(10));
          data.append('source_id', this.sourceId);
          data.append('priority_id', this.priorityId);
          data.append('is_enable_poi_after_upload', this.isEnablePoiAfterUpload);

          this.$requestHelpers
            .post('api/admin/videodata',
              data,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              })
            .then((response) => {
              this.$emit('videoWasUploaded', response);
            }).catch((error) => {
              this.$emit('errorUpload', this.fetchError(error));
            }).finally(() => {
              this.loading = false;
              this.disabled = false;
              this.file = null;
              this.hideVideoUploadDialog();
            });
        }
      }
    },

    hideVideoUploadDialog() {
      this.file = null;
      this.isUploadStart = false;
      this.isFileSelected = false;
      this.sourceId = null;
      this.priorityId = 5;
      this.isVideoPreprocessError = false;
      this.isEnablePoiAfterUpload = false;
      this.isResolutionOk = false;
      if (this.$refs.poiVimeoDirectUpload) {
        this.$refs.poiVimeoDirectUpload.clearFile();
      }
      this.$emit('update:show', false);
    },

    async preprocessVideoFile(file) {
      this.isFileSelected = !!file;
      try {
        this.isResolutionOk = await this.isResolutionEnough(
          file,
        );
        this.isVideoPreprocessError = false;
      } catch (e) {
        this.isResolutionOk = true;
        this.isVideoPreprocessError = true;
      }
    },

    addVideoItem(item) {
      this.hideVideoUploadDialog();
      this.disabled = false;
      this.$emit('videoWasSaved', item);
    },
  },

  computed: {
    enableUpload() {
      return this.isFileSelected
        && this.sourceId
        && !this.isUploadStart
        && this.isResolutionOk;
    },
  },

  watch: {
    async file(file) {
      await this.preprocessVideoFile(file);
    },
  },
};
</script>

<style scoped>

</style>
